import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Company } from "@/types";
import { type Ref, ref, watch } from "vue";

interface CompanySectionFormOptions<T> {
  /** The company being edited. Passed as a getter so the form follows the prop. */
  company: () => Company | null;
  /** Path segment under /api/admin/company/{id}/, e.g. "fee-config". GET loads it, PUT saves it. */
  resource: string;
  /** Copies a loaded payload into the component's form state. */
  apply: (data: T) => void;
  /** The body to PUT, read from the component's form state. */
  payload: () => unknown;
  /** Success toast after a save, as [title, body]. */
  savedMessage: [string, string];
}

export interface CompanySectionForm {
  isLoading: Ref<boolean>;
  isSaving: Ref<boolean>;
  loadFailed: Ref<boolean>;
  load: () => Promise<void>;
  save: () => Promise<void>;
}

/**
 * Load/save orchestration for an admin section that edits one company resource.
 *
 * Both admin sections built on this behave the same way around their form: a failed GET leaves the
 * form unrendered (saving against unhydrated state would write blank defaults over the real
 * configuration), and a response that arrives after the admin has switched company is discarded
 * instead of being applied to the new company's form.
 *
 * `apply` therefore only ever runs for the newest request, so a component never checks for a stale
 * response itself. It runs for the PUT's response too: both verbs of these resources answer with
 * the same payload, so a save also shows the values as stored — e.g. a rate the backend rounded to
 * its column's scale. Because `apply` replaces every field, there is no separate reset step.
 */
export function useCompanySectionForm<T>({
  company,
  resource,
  apply,
  payload,
  savedMessage,
}: CompanySectionFormOptions<T>): CompanySectionForm {
  const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadFailed = ref(false);
  // Only the newest load may touch the form: switching company fires overlapping GETs, and the
  // earlier one can answer last.
  let loadRequestId = 0;

  const url = () => `/api/admin/company/${company()?.id}/${resource}`;

  const load = async () => {
    if (!company()?.id) return;

    loadFailed.value = false;
    isLoading.value = true;
    const requestId = ++loadRequestId;
    try {
      const res = await goSolidApi.get(url());
      if (requestId !== loadRequestId) return;
      apply(res.data);
    } catch (error: unknown) {
      if (requestId !== loadRequestId) return;
      loadFailed.value = true;
      globalErrorHandler(error);
    } finally {
      if (requestId === loadRequestId) {
        isLoading.value = false;
      }
    }
  };

  const save = async () => {
    if (!company()?.id) return;

    isSaving.value = true;
    const requestId = loadRequestId;
    try {
      const res = await goSolidApi.put(url(), payload());
      if (requestId === loadRequestId) apply(res.data);
      globalSuccessHandler(...savedMessage);
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      isSaving.value = false;
    }
  };

  // Keyed on the id, not the object: the account store hands over new company objects with every
  // answer it takes, a reload after a signed document included, and reloading then would replace
  // what the admin is typing. The resource is read by the company's id alone.
  watch(() => company()?.id, load, { immediate: true });

  return { isLoading, isSaving, loadFailed, load, save };
}
